import axiosInstance from '@/axios';
import {
    type UploadIdCardOutput,
    UploadIdCardOutputSchema,
} from 'request-form-lib';
import { UploadIdCardPublicInput, UploadIdCardPublicInputSchema } from '@/Schemas/MagicLink/UploadIdCardPublicInput';
import { GetMagicLinkDataInput, GetMagicLinkDataInputSchema } from '@/Schemas/MagicLink/GetMagicLinkDataInput';
import { GetMagicLinkDataOutput, GetMagicLinkDataOutputSchema } from '@/Schemas/MagicLink/GetMagicLinkDataOutput';
import { CancelMagicLinkInput, CancelMagicLinkInputSchema } from '@/Schemas/MagicLink/CancelMagicLinkInput';
import { ConfirmMagicLinkInput, ConfirmMagicLinkInputSchema } from '@/Schemas/MagicLink/ConfirmMagicLinkInput';
import { getToken } from '@/Provider/MagicLinkAppProvider';

/**
 * Gets magic link data for the given token hash.
 * 
 * @param linkTokenHash - The token hash from the URL
 * @returns Promise that resolves to the magic link data
 */
export const getMagicLinkData = async (linkTokenHash: string): Promise<GetMagicLinkDataOutput> => {
    const input: GetMagicLinkDataInput = GetMagicLinkDataInputSchema.parse({ linkTokenHash });
    const url = "api/magic-link/public/getMagicLinkData";
    const response = await axiosInstance.post(url, input);
    return GetMagicLinkDataOutputSchema.parse(response.data);
};

/**
 * Confirms a magic link request.
 * 
 * @param linkTokenHash - The token hash from the URL
 * @returns Promise that resolves when the confirmation is complete
 */
export const confirmMagicLink = async (linkTokenHash: string): Promise<void> => {
    const input: ConfirmMagicLinkInput = ConfirmMagicLinkInputSchema.parse({ linkTokenHash });
    const url = "api/magic-link/public/confirmMagicLink";
    await axiosInstance.post(url, input);
};

/**
 * Cancels a magic link request.
 * 
 * @param linkTokenHash - The token hash from the URL
 * @returns Promise that resolves when the cancellation is complete
 */
export const cancelMagicLink = async (linkTokenHash: string): Promise<void> => {
    const input: CancelMagicLinkInput = CancelMagicLinkInputSchema.parse({ linkTokenHash });
    const url = "api/magic-link/public/cancelMagicLink";
    await axiosInstance.post(url, input);
};

/**
 * Uploads an ID card with validation.
 * This function handles the API call to upload and validate an ID card image.
 * 
 * @param input - The upload input containing file data and metadata
 * @returns Promise that resolves to the upload result with validation and extracted data
 */
export const uploadIdCardWithValidation = async (input: UploadIdCardPublicInput): Promise<UploadIdCardOutput> => {
    const url = "api/magic-link/public/uploadIdCardWithValidation";
    const payload = UploadIdCardPublicInputSchema.parse({
        ...input,
        linkTokenHash: input.linkTokenHash ?? getToken(),
    });
    const response = await axiosInstance.post(url, payload);
    return UploadIdCardOutputSchema.parse(response.data);
};
